import { useState, useCallback } from "react";

export interface Toast {
  id: string;
  title?: string;
  description?: string;
  variant?: "default" | "destructive" | "success";
  duration?: number;
}

type ToastInput = Omit<Toast, "id">;

let toastCount = 0;

const listeners = new Set<(toasts: Toast[]) => void>();
let memoryToasts: Toast[] = [];

function dispatch(toasts: Toast[]) {
  memoryToasts = toasts;
  listeners.forEach((listener) => listener(toasts));
}

export function toast(input: ToastInput) {
  const id = String(++toastCount);
  const newToast: Toast = { ...input, id };

  dispatch([...memoryToasts, newToast]);

  const duration = input.duration ?? 4000;
  setTimeout(() => {
    dispatch(memoryToasts.filter((t) => t.id !== id));
  }, duration);

  return id;
}

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>(memoryToasts);

  useState(() => {
    listeners.add(setToasts);
    return () => {
      listeners.delete(setToasts);
    };
  });

  const dismiss = useCallback((id: string) => {
    dispatch(memoryToasts.filter((t) => t.id !== id));
  }, []);

  return { toasts, toast, dismiss };
}
